import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import type { ApexOptions } from "apexcharts";
import { useTranslation, useToast } from "@repo/core";
import type { StrategyTimeline } from "../../../types/strategy-detail";
import {
  useDeleteStrategy,
  useUpdateStrategy,
  type PerformanceChartData,
  type PortfolioType,
  type Strategy,
} from "@repo/api";
import {
  useBenchmarks,
  useUniverseDetail,
} from "../../../hooks/api/useUniversesApi";
import StrategyActionButtons from "./StrategyActionButtons";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useStrategyDetailType } from "../../../hooks/useStrategyDetailType";
import LoadingHeader from "./LoadingHeader";
import MissingHeader from "./MissingHeader";
import AttributesHeader from "./AttributesHeader";
import ConfirmationModal from "./ConfirmationModal";
import EditModal from "./EditModal";
import NoteModal from "./NoteModal";
import NoteStrip from "./NoteStrip";
import NoteDeleteConfirmModal from "./NoteDeleteConfirmModal";
import ChartLegend from "./ChartLegend";
import SmallScreenActionMenu from "./SmallScreenActionMenu";
import ActionButtons from "./ActionButtons";
import TimelineChart from "./TimelineChart";

// Single source of truth for series colors, shared between the chart's own
// `colors` option and ChartLegend's swatches - keep these in sync by index
// with how chartSeries actually orders its entries below.
export const CHART_COLORS = ["#3B82F6", "#10B981"] as const;

interface StrategyHeaderProps {
  portfolio?: PortfolioType;
  timeline: StrategyTimeline | null;
  chartData?: PerformanceChartData;
  isStrategyLoading?: boolean;
  isTimelineLoading?: boolean;
  isChartDataLoading?: boolean;
  dateRange?: string;
  className?: string;
  onOpenStrategySettings?: () => void;
  onStrategyUpdate?: (updatedStrategy: Partial<Strategy>) => void;
  onBenchmarkChange?: (benchmark: string) => void;
}

/**
 * Strategy header component showing KPIs and performance chart
 */
export const StrategyHeader = ({
  portfolio,
  timeline,
  chartData,
  isStrategyLoading = false,
  isTimelineLoading = false,
  isChartDataLoading = false,
  dateRange,
  className = "",
  onOpenStrategySettings,
  onStrategyUpdate,
  onBenchmarkChange,
}: StrategyHeaderProps) => {
  const { t, tStrategy, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editStrategyName, setEditStrategyName] = useState("");
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [showNoteDeleteConfirm, setShowNoteDeleteConfirm] = useState(false);
  const [selectedBenchmark, setSelectedBenchmark] = useState<string>("SP500");
  const [isBenchmarkDropdownOpen, setIsBenchmarkDropdownOpen] = useState(false);
  const universeQuery = useUniverseDetail(portfolio?.poolName || "");
  const menuRef = useRef<HTMLDivElement>(null);
  const benchmarkDropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Use custom hook to determine strategy detail type and actions
  const { actions } = useStrategyDetailType(portfolio);
  // Tracks whether this component currently owes the ToastContext a
  // resumeToasts() call, so the pause from opening the remove modal is
  // guaranteed to be released exactly once - including if the user
  // navigates away (e.g. browser back) instead of using Confirm/Cancel.
  const toastsPausedRef = useRef(false);

  const { mutate: deleteStrategy, isPending: isDeleting } = useDeleteStrategy(
    portfolio?.modelId || ""
  );
  const { mutate: updateStrategy, isPending: isUpdating } = useUpdateStrategy();
  const { addSuccessMessage, addErrorMessage, pauseToasts, resumeToasts } =
    useToast();
  const queryClient = useQueryClient();

  const benchmarkQuery = useBenchmarks();
  const availableBenchmarks = useMemo(() => {
    if (benchmarkQuery.data) {
      return Object.keys(benchmarkQuery.data).reduce(
        (acc, key) => {
          const benchmark = benchmarkQuery.data as Record<
            string,
            { universe: string; name: string }
          >;
          if (benchmark[key]?.universe === "sp500_historical") {
            return acc;
          }
          return [
            ...acc,
            {
              value:
                benchmark[key]?.universe === "fama"
                  ? key
                  : benchmark[key]?.universe.toUpperCase(),
              name: benchmark[key]?.name || key,
            },
          ];
        },
        [] as {
          value: string;
          name: string;
        }[]
      );
    } else {
      return [];
    }
  }, [benchmarkQuery.data]);

  const poolName = useMemo(
    () => universeQuery.data?.universe,
    [universeQuery.data]
  );

  // Set initial benchmark when data loads
  useEffect(() => {
    if (availableBenchmarks.length > 0 && !selectedBenchmark) {
      const initialBenchmark = availableBenchmarks[0].value;
      setSelectedBenchmark(initialBenchmark);

      // Notify parent of initial benchmark
      if (onBenchmarkChange) {
        onBenchmarkChange(initialBenchmark);
      }
    }
  }, [availableBenchmarks, selectedBenchmark, onBenchmarkChange]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Releases the toast pause exactly once, however the modal closes.
  const releaseToastPause = useCallback(() => {
    if (toastsPausedRef.current) {
      toastsPausedRef.current = false;
      resumeToasts();
    }
  }, [resumeToasts]);

  const handleRemoveClick = () => {
    // Suppress unrelated toasts (e.g. a stray "backtest completed" WebSocket
    // notification) while the delete confirmation is on screen - resumed
    // synchronously below before any toast this flow itself wants to show.
    pauseToasts();
    toastsPausedRef.current = true;
    setShowRemoveModal(true);
  };

  const handleRemoveConfirm = () => {
    if (!portfolio?.modelId) {
      releaseToastPause();
      addErrorMessage(tStrategy("removeModal.missingId"));
      setShowRemoveModal(false);
      return;
    }

    deleteStrategy(
      { modelId: portfolio.modelId },
      {
        onSuccess: () => {
          queryClient.setQueryData(["model"], (oldData: Strategy[]) =>
            oldData.filter((model) => model.modelId !== portfolio.modelId)
          );
          queryClient.invalidateQueries({
            queryKey: ["model", portfolio.modelId],
          });
          setShowRemoveModal(false);
          releaseToastPause();
          addSuccessMessage(tStrategy("removeModal.success"));
          navigate("/");
        },
        onError: (error) => {
          console.error("Error deleting portfolio:", error);
          releaseToastPause();
          addErrorMessage(tStrategy("removeModal.error"));
          setShowRemoveModal(false);
        },
      }
    );
  };

  const handleRemoveCancel = () => {
    releaseToastPause();
    setShowRemoveModal(false);
  };

  // Safety net: if this component unmounts while the modal is still open
  // (e.g. the user navigates away instead of clicking Confirm/Cancel), make
  // sure the pause doesn't leak and suppress toasts app-wide afterward.
  useEffect(() => {
    return () => releaseToastPause();
  }, [releaseToastPause]);

  const handleEditClick = () => {
    setEditStrategyName(portfolio?.name || "");
    setShowEditModal(true);
  };

  const handleEditSave = () => {
    if (!portfolio?.modelId) {
      addErrorMessage(tStrategy("editModal.missingId"));
      setShowEditModal(false);
      return;
    }

    if (!editStrategyName.trim()) {
      addErrorMessage(tStrategy("editModal.emptyName"));
      return;
    }

    const updatedStrategyData = { name: editStrategyName.trim() };

    updateStrategy(
      {
        modelId: portfolio.modelId,
        name: editStrategyName.trim(),
      },
      {
        onSuccess: (data: Partial<Strategy>) => {
          setShowEditModal(false);
          queryClient.setQueryData(["model", portfolio.modelId], () => [data]);
          queryClient.setQueryData(["model"], (oldData: Strategy[]) => {
            return (oldData || []).map((s) =>
              s.modelId === data.modelId ? data : s
            );
          });
          addSuccessMessage(tStrategy("editModal.success"));
          // Call the callback to update parent's local data if provided
          onStrategyUpdate?.(updatedStrategyData);
        },
        onError: (error) => {
          console.error("Error updating strategy:", error);
          addErrorMessage(tStrategy("editModal.error"));
        },
      }
    );
  };

  const handleEditCancel = () => {
    setShowEditModal(false);
    setEditStrategyName("");
  };

  const hasNote = !!portfolio?.note;

  const handleNoteEditClick = () => {
    setNoteDraft(portfolio?.note || "");
    setShowNoteModal(true);
  };

  // Shared by save and delete - both are just a PATCH of the note field to a
  // different value, so the mutation call and cache update only need to be
  // written once.
  const persistNote = (
    noteValue: string,
    successMessage: string,
    errorMessage: string,
    onSettled: () => void
  ) => {
    if (!portfolio?.modelId) {
      addErrorMessage(tStrategy("note.missingId"));
      onSettled();
      return;
    }

    updateStrategy(
      {
        modelId: portfolio.modelId,
        note: noteValue,
      },
      {
        onSuccess: (data: Partial<Strategy>) => {
          onSettled();
          queryClient.setQueryData(["model", portfolio.modelId], () => [data]);
          queryClient.setQueryData(["model"], (oldData: Strategy[]) => {
            return (oldData || []).map((s) =>
              s.modelId === data.modelId ? data : s
            );
          });
          addSuccessMessage(successMessage);
          onStrategyUpdate?.({ note: noteValue });
        },
        onError: (error) => {
          console.error("Error updating note:", error);
          addErrorMessage(errorMessage);
        },
      }
    );
  };

  const handleNoteSave = () => {
    persistNote(
      noteDraft,
      tStrategy("note.updateSuccess"),
      tStrategy("note.updateError"),
      () => setShowNoteModal(false)
    );
  };

  const handleNoteCancel = () => {
    setShowNoteModal(false);
    setNoteDraft("");
  };

  const handleNoteDeleteClick = () => {
    setShowNoteDeleteConfirm(true);
  };

  const handleNoteDeleteConfirm = () => {
    persistNote(
      "",
      tStrategy("note.deleteSuccess"),
      tStrategy("note.deleteError"),
      () => setShowNoteDeleteConfirm(false)
    );
  };

  const handleNoteDeleteCancel = () => {
    setShowNoteDeleteConfirm(false);
  };

  const handleShowDraftClick = () => {
    navigate(`/${portfolio?.modelId}/draft`);
  };

  const handleShowActiveClick = () => {
    navigate(`/${portfolio?.modelId.replace("_draft", "")}`);
  };

  const toggleBenchmarkDropdown = () => {
    setIsBenchmarkDropdownOpen(!isBenchmarkDropdownOpen);
  };

  const handleBenchmarkSelect = (benchmark: string) => {
    setSelectedBenchmark(benchmark);
    setIsBenchmarkDropdownOpen(false);

    // Notify parent component about benchmark change
    if (onBenchmarkChange) {
      onBenchmarkChange(benchmark);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
      if (
        benchmarkDropdownRef.current &&
        !benchmarkDropdownRef.current.contains(event.target as Node)
      ) {
        setIsBenchmarkDropdownOpen(false);
      }
    };

    if (isMenuOpen || isBenchmarkDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen, isBenchmarkDropdownOpen]);

  // Chart configuration
  const chartOptions: ApexOptions = useMemo(() => {
    // Calculate real-time monitoring start date for annotations
    let realTimeMonitoringStartTimestamp: number | null = null;
    let lastDataTimestamp: number | null = null;

    if (portfolio?.backtest?.realTimeMonitoringStart && chartData?.Strategy) {
      realTimeMonitoringStartTimestamp = new Date(
        portfolio.backtest.realTimeMonitoringStart
      ).getTime();

      // Get the last timestamp from the strategy data
      const strategyDates = Object.keys(chartData.Strategy);
      if (strategyDates.length > 0) {
        const sortedDates = strategyDates.sort();
        lastDataTimestamp = new Date(
          sortedDates[sortedDates.length - 1]
        ).getTime();
      }
    }

    return {
      chart: {
        type: "line",
        height: 350,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        background: "transparent",
        defaultLocale: i18n.language === 'cs' ? 'cs' : 'en',
        locales: [
          {
            name: 'en',
            options: {
              months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
              shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
              days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
              shortDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
              toolbar: {
                download: 'Download SVG',
                selection: 'Selection',
                selectionZoom: 'Selection Zoom',
                zoomIn: 'Zoom In',
                zoomOut: 'Zoom Out',
                pan: 'Panning',
                reset: 'Reset Zoom'
              }
            }
          },
          {
            name: 'cs',
            options: {
              months: ['Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen', 'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'],
              shortMonths: ['Led', 'Úno', 'Bře', 'Dub', 'Kvě', 'Čvn', 'Čvc', 'Srp', 'Zář', 'Říj', 'Lis', 'Pro'],
              days: ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota'],
              shortDays: ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'],
              toolbar: {
                download: 'Stáhnout SVG',
                selection: 'Výběr',
                selectionZoom: 'Výběr pro zoom',
                zoomIn: 'Přiblížit',
                zoomOut: 'Oddálit',
                pan: 'Posun',
                reset: 'Reset zoomu'
              }
            }
          }
        ]
      },
      stroke: {
        curve: "smooth",
        width: 2,
      },
      colors: [...CHART_COLORS],
      xaxis: {
        type: "datetime",
        labels: {
          format: "MMM yyyy",
        },
      },
      yaxis: {
        labels: {
          formatter: (value) => `$${value.toLocaleString()}`,
        },
      },
      grid: {
        borderColor: "#e5e7eb",
      },
      legend: {
        show: false,
      },
      tooltip: {
        shared: true,
        intersect: false,
        x: {
          format: "dd MMM yyyy",
        },
        y: {
          formatter: (value) => `$${value.toLocaleString()}`,
        },
        custom: ({ series, dataPointIndex, w }) => {
          const currentLocale = i18n.language || 'en';
          const currentTimestamp = w.globals.seriesX[0][dataPointIndex];
          const date = new Date(currentTimestamp).toLocaleDateString(
            currentLocale === 'cs' ? 'cs-CZ' : 'en-US',
            {
              year: "numeric",
              month: "short",
              day: "numeric",
            }
          );

          // Check if this data point is in Real Time Monitoring period
          const isRealTimeMonitoring =
            realTimeMonitoringStartTimestamp &&
            currentTimestamp >= realTimeMonitoringStartTimestamp;

          let tooltipContent = `
            <div class="bg-white text-sm rounded-lg shadow-lg border border-gray-200 min-w-[200px] overflow-hidden">
              <div class="bg-gray-100 px-3 py-2 font-semibold text-gray-800 border-b border-gray-200">
                ${date}
                ${isRealTimeMonitoring ? `<div class="text-xs font-normal text-blue-600 mt-0.5">${tStrategy('realTimeMonitoring')}</div>` : ''}
              </div>
              <div class="p-3">
          `;

          // Add each series value
          series.forEach((seriesData: number[], index: number) => {
            const rawSeriesName = w.globals.seriesNames[index];
            const value = seriesData[dataPointIndex];
            const color = w.globals.colors[index];

            // Map technical benchmark names to readable descriptions
            const getReadableSeriesName = (name: string) => {
              // Find the benchmark in availableBenchmarks to get the readable name
              const benchmark = availableBenchmarks.find(
                (b) => b.value === name
              );
              return benchmark ? benchmark.name : name;
            };

            const seriesName = getReadableSeriesName(rawSeriesName);

            if (value !== undefined && value !== null) {
              tooltipContent += `
                <div class="flex items-center justify-between mb-1">
                  <div class="flex items-center gap-2">
                    <div class="w-3 h-3 rounded-full" style="background-color: ${color}"></div>
                    <span class="text-gray-600">${seriesName}:</span>
                  </div>
                  <span class="font-semibold text-gray-900">$${value.toLocaleString()}</span>
                </div>
              `;
            }
          });

          tooltipContent += `</div>`;

          tooltipContent += `</div>`;
          return tooltipContent;
        },
      },
      annotations: {
        xaxis:
          realTimeMonitoringStartTimestamp &&
            lastDataTimestamp &&
            realTimeMonitoringStartTimestamp < lastDataTimestamp
            ? [
              {
                x: realTimeMonitoringStartTimestamp,
                x2: lastDataTimestamp,
                fillColor: "#3B82F6",
                opacity: 0.1,
              },
            ]
            : [],
      },
    };
  }, [
    portfolio?.backtest?.realTimeMonitoringStart,
    chartData?.Strategy,
    availableBenchmarks,
    i18n.language,
    tStrategy,
  ]);

  // Available benchmark options
  // const benchmarkOptions = ["S&P 100", "S&P 500", "DAX"];

  const chartSeries = useMemo(() => {
    if (!chartData) return [];

    const series = [];

    // Always add Strategy series if available
    if (chartData.Strategy) {
      const strategyData = Object.entries(chartData.Strategy).map(
        ([date, value]) => ({
          x: new Date(date).getTime(),
          y: value,
        })
      );

      series.push({
        name: portfolio?.name || "Strategy",
        data: strategyData,
      });
    }

    // Add selected benchmark series if available
    if (selectedBenchmark && chartData[selectedBenchmark]) {
      const benchmarkData = Object.entries(chartData[selectedBenchmark]).map(
        ([date, value]) => ({
          x: new Date(date).getTime(),
          y: value,
        })
      );

      series.push({
        name: selectedBenchmark,
        data: benchmarkData,
      });
    }

    return series;
  }, [chartData, selectedBenchmark, portfolio?.name]);

  // Mirror chartSeries' conditional ordering so the legend always matches
  // whichever colors ApexCharts actually assigned by position.
  const hasStrategySeries = !!chartData?.Strategy;
  const strategyColor = hasStrategySeries ? CHART_COLORS[0] : undefined;
  const benchmarkColor = CHART_COLORS[hasStrategySeries ? 1 : 0];

  if (isStrategyLoading) {
    return <LoadingHeader className={className} />;
  }

  if (!portfolio) {
    return <MissingHeader className={className} />;
  }

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}
    >
      {/* Mobile Action Buttons - visible only on small screens */}
      <div className="lg:hidden mb-4">
        <div className="space-y-2">
          {/* Settings button - alone on its own row */}
          <div>
            <button
              onClick={onOpenStrategySettings}
              className="w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 transition-all duration-200 border hover:shadow-md hover:scale-105 flex items-center justify-center gap-2 cursor-pointer"
              style={{
                backgroundColor: "#EBF5FF",
                borderColor: "#A4CAFE",
                color: "#1A55DB",
              }}
              data-tour="strategy-settings-button"
            >
              <span className="text-sm font-medium">
                {tStrategy("settings.title")}
              </span>
            </button>
          </div>

          {/* Action buttons */}
          <StrategyActionButtons
            strategy={portfolio}
            dateRange={dateRange}
            layout="mobile"
          />
        </div>
      </div>

      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <p className="text-sm text-gray-600 mb-1">
            {t("strategy.indicatorLabel")} {Object.keys(portfolio.factor)[0]}
            {" | "}Universe: {poolName || portfolio.poolName}
          </p>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-gray-900">
              {portfolio.name}
            </h2>

            {/* Edit name and Remove buttons - visible only on large screens and when no dateRange */}
            {!dateRange && (
              <ActionButtons
                actions={actions}
                handleEditClick={handleEditClick}
                handleRemoveClick={handleRemoveClick}
                handleShowDraftClick={handleShowDraftClick}
                handleShowActiveClick={handleShowActiveClick}
                handleAddNoteClick={handleNoteEditClick}
                hasNote={hasNote}
                modelId={portfolio?.modelId}
              />
            )}

            {/* Three dots menu - visible only on small screens */}
            {/* Three dots menu - visible only on small screens and when no dateRange */}
            {!dateRange && (
              <SmallScreenActionMenu
                actions={actions}
                handleEditClick={handleEditClick}
                handleRemoveClick={handleRemoveClick}
                handleShowDraftClick={handleShowDraftClick}
                handleShowActiveClick={handleShowActiveClick}
                handleAddNoteClick={handleNoteEditClick}
                hasNote={hasNote}
                setIsMenuOpen={setIsMenuOpen}
                isMenuOpen={isMenuOpen}
                toggleMenu={toggleMenu}
                menuRef={menuRef}
                modelId={portfolio?.modelId}
              />
            )}
          </div>
        </div>

        {/* Action buttons on the right - hidden on small screens */}
        <StrategyActionButtons
          strategy={portfolio}
          dateRange={dateRange}
          layout="desktop"
          className="hidden lg:flex ml-4"
        />
      </div>

      {portfolio.note && (
        <NoteStrip
          note={portfolio.note}
          canEdit={actions.showEdit}
          onEditClick={handleNoteEditClick}
          onDeleteClick={handleNoteDeleteClick}
        />
      )}

      {/* Horizontal divider */}
      <hr className="border-gray-200 mb-6" />

      {/* KPI Section */}
      <AttributesHeader chartData={chartData} portfolio={portfolio} />

      {/* Chart Section */}
      <div>
        <TimelineChart
          isLoading={isTimelineLoading || isChartDataLoading}
          chartData={chartData}
          chartOptions={chartOptions}
          chartSeries={chartSeries}
        />

        {/* Custom Legend */}
        {timeline && (
          <ChartLegend
            timeline={timeline}
            availableBenchmarks={availableBenchmarks}
            selectedBenchmark={selectedBenchmark}
            isBenchmarkDropdownOpen={isBenchmarkDropdownOpen}
            toggleBenchmarkDropdown={toggleBenchmarkDropdown}
            handleBenchmarkSelect={handleBenchmarkSelect}
            benchmarkDropdownRef={benchmarkDropdownRef}
            strategyColor={strategyColor}
            benchmarkColor={benchmarkColor}
          />
        )}
      </div>

      {/* Remove Confirmation Modal */}
      {showRemoveModal && (
        <ConfirmationModal
          isDeleting={isDeleting}
          handleRemoveConfirm={handleRemoveConfirm}
          handleRemoveCancel={handleRemoveCancel}
          modelId={portfolio?.modelId}
        />
      )}

      {/* Edit Strategy Name Modal */}
      {showEditModal && (
        <EditModal
          isUpdating={isUpdating}
          editStrategyName={editStrategyName}
          setEditStrategyName={setEditStrategyName}
          handleEditCancel={handleEditCancel}
          handleEditSave={handleEditSave}
        />
      )}

      {/* Add/Edit Portfolio Note Modal */}
      {showNoteModal && (
        <NoteModal
          isSaving={isUpdating}
          note={noteDraft}
          setNote={setNoteDraft}
          handleNoteCancel={handleNoteCancel}
          handleNoteSave={handleNoteSave}
        />
      )}

      {/* Delete Portfolio Note Confirmation Modal */}
      {showNoteDeleteConfirm && (
        <NoteDeleteConfirmModal
          isDeleting={isUpdating}
          handleNoteDeleteConfirm={handleNoteDeleteConfirm}
          handleNoteDeleteCancel={handleNoteDeleteCancel}
        />
      )}
    </div>
  );
};

export default StrategyHeader;
